import clsx from "clsx";
import { Col, ModalProps, Typography, useModal, useToast } from "@commonlibs";
import { createModal } from "@commonlibs";
import Modal from "ui/common/components/feedback/Modal/Modal";
import ModalFooter from "ui/common/components/feedback/Modal/ModalFooter/ModalFooter";
import useTranslate from "ui/locale/hooks/useTranslate";
import FileUploader from "ui/partner/container/FileUploader/FileUploader";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { Queries } from "ui/query/queries";
import useGetPartnerGiftCardStrategies from "features/giftcards/hooks/useGetPartnerGiftCardStrategies";
import { ModalGiftCardsSelect } from "../common/GiftCardsModal.styles";
import useLoadGiftCards from "features/giftcards/hooks/useLoadGiftCards";

const LoadGiftCardsModal = createModal<ModalProps>(({ className, ...rest }) => {
    const translate = useTranslate();
    const { hideModal } = useModal();
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    const [partnerStrategies, setPartnerStrategies] = useState<{ label: string; value: string }[]>([]);
    const [uploadedFile, setUploadedFile] = useState<File>();
    const [selectedPartnerAddress, setSelectedPartner] = useState<string>();
    const { data: partnerGiftCardStrategies } = useGetPartnerGiftCardStrategies();

    const { mutate, isLoading } = useLoadGiftCards({ onSuccess: handleOnSuccess });

    useEffect(() => {
        queryClient.invalidateQueries(Queries.PARTNER_FIND_ALL_WITHOUT_PARENT);
    }, [queryClient]);

    useEffect(() => {
        if (partnerGiftCardStrategies) {
            setPartnerStrategies(partnerGiftCardStrategies.map((partner) => ({ label: partner.name, value: partner.address })));
        }
    }, [partnerGiftCardStrategies]);

    function closeModal() {
        hideModal(LoadGiftCardsModal.id);
    }

    function handleOnLoadDoc(file: File) {
        setUploadedFile(file);
    }

    function handleOnSuccess() {
        showToast(translate("giftCardsLoadedSuccessfully"), { type: "success" });
        closeModal();
    }

    const isValidForm = () => {
        return Boolean(uploadedFile) && Boolean(selectedPartnerAddress);
    };

    function handleOnSubmit() {
        if (!isValidForm()) return;
        const formData = buildFormData();
        mutate({ payload: formData });
    }

    function buildFormData() {
        const formData = new FormData();
        const fields = {
            partner: selectedPartnerAddress,
        };

        Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, String(value));
        });

        if (uploadedFile) {
            formData.append("file", uploadedFile);
        }
        return formData;
    }

    return (
        <Modal
            title={translate("loadingVouchers")}
            className={clsx("load-giftcards-modal", className)}
            style={{ width: "550px" }}
            {...rest}
        >
            <Col gap={20}>
                <ModalGiftCardsSelect
                    value={selectedPartnerAddress}
                    placeholder={translate("choosePartnerStrategy")}
                    label={translate("partner")}
                    onChange={(e) => setSelectedPartner(e)}
                    options={partnerStrategies}
                />
                <Col gap={10}>
                    <Typography variant="body3Strong">{translate("giftcardFileUploaderExplanation")}</Typography>
                    <FileUploader
                        onDeleteDocument={() => setUploadedFile(undefined)} // skipcq: JS-W1042
                        onLoadDocument={handleOnLoadDoc}
                        showExplanationSection={false}
                        fileTypes=".xlsx,.xls,.csv,.xlsm"
                    />
                </Col>
                <ModalFooter
                    isLoading={isLoading}
                    cancelLabel={translate("cancel")}
                    mainLabel={translate("send")}
                    onCancel={closeModal}
                    onSubmit={handleOnSubmit}
                    disabled={!isValidForm()}
                />
            </Col>
        </Modal>
    );
});

export default LoadGiftCardsModal;
